import { TbCopyPlus } from 'react-icons/tb'
import React from 'react'

import { statusFieldView } from '@/Ship/Components/UI/StatusBadge/statusField'
import { ACTIVE_TASK_STATUSES } from '@/Ship/Contracts/Statuses/TaskStatus'
import { DEFAULT_DETAIL_WIDTH } from '@/Ship/Components/UI/AppDrawer/AppDrawer'

import { shiftLabel } from '../Helpers/shiftField'
import { buildAssignmentViewButtons } from '../Templates/assignmentButtons'
import { ASSIGNMENT_STATUS_OPTIONS, renderDelayStatus, renderStatusTag } from '../Templates/assignmentCells'
import { AssignmentCreateTemplate } from '../Templates/AssignmentCreateTemplate'
import { AssignmentViewTemplate } from '../Templates/AssignmentViewTemplate'

import type { IResourceConfig } from '@/Core/Contracts/UI'

/** Registry key of the "Задачі без заявок" list page (see AssignmentsWithoutRequestsPage). */
const WITHOUT_REQUESTS_RESOURCE_KEY = 'Assignment.WithoutRequests'

export const AssignmentResource: IResourceConfig = {
    title: 'Завдання',
    key: 'Assignment',
    restUrl: {
        getCollection: '/services/executor-assignments',
        get: '/services/executor-assignments',
        post: '/services/executor-assignments',
        patch: '/services/executor-assignments',
        delete: '/services/executor-assignments',
    },
    fields: {
        id: {
            label: 'ID',
            type: 'number',
            inputProps: {
                disabled: true,
            },
            /** У фільтрі: оператор (=, >, ≥, <, ≤) або діапазон від–до */
            isFilterRange: true,
        },
        status: {
            label: 'Статус',
            type: 'status',
            options: ASSIGNMENT_STATUS_OPTIONS,
            /** У картці запису статус малює Ship-бейдж із меню зміни */
            view: statusFieldView,
            /** У фільтрі можна обрати кілька статусів */
            isFilterMultiple: true,
            isEditable: false,
        },
        isHired: {
            label: 'Найманий',
            type: 'switch',
        },
        // Responsible dispatcher of a standalone task. Stored on the task's executor
        // block on the backend (assignment has no dispatcher column); the form maps
        // executor.dispatcher ↔ this field. Distinct from `filterDispatcher` (table filter).
        // isRequired is set reactively in AssignmentCreateTemplate (required only for
        // standalone tasks, where the field is actually shown) — NOT statically here,
        // or an ERP task would fail validation on a hidden field. Mirrors `carrier`.
        dispatcher: {
            label: 'Відповідальний диспетчер',
            type: 'select',
            resource: 'Setting.User',
            inputProps: {
                filter: { filterRoles: { op: 'like', value: 'ROLE_DISPATCHER' }, status: 'active' },
                responseLabel: (item: any) =>
                    item?.fullName || `${item?.lastName || ''} ${item?.firstName || ''}`.trim() || '—',
            },
        },
        carrier: {
            label: 'Перевізник',
            type: 'select',
            resource: 'Setting.ListCarrier',
            inputProps: {
                filter: { isActive: true, isOwn: false },
            },
            /** У фільтрі — кілька перевізників (carrier.uuid in […]) */
            isFilterMultiple: true,
            /** Опції фільтра: і власні перевізники — у таблиці є задачі з ними (у формі — лише чужі) */
            filterOptionsFilter: { isActive: true },
        },
        vehicle: {
            label: 'Транспортний засіб',
            type: 'select',
            resource: 'Transport.Vehicle',
            /** У фільтрі — кілька ТЗ (vehicle.uuid in […]) */
            isFilterMultiple: true,
        },
        // Кластер і регіон задачі (обов'язкові). Регіон залежить від кластера: у формах
        // його опції звужує cluster.code обраного кластера (Helpers/clusterRegion).
        cluster: {
            label: 'Кластер',
            type: 'select',
            resource: 'Setting.ListCluster',
            isRequired: true,
            inputProps: {
                filter: { isActive: true },
            },
            /** У фільтрі — кілька кластерів (cluster.uuid in […]) */
            isFilterMultiple: true,
            /** Опції фільтра: і неактивні — задачі лишаються за ними */
            filterOptionsFilter: { isRemoved: false },
        },
        region: {
            label: 'Регіон',
            type: 'select',
            resource: 'Setting.ListRegion',
            isRequired: true,
            inputProps: {
                filter: { isActive: true },
            },
            /** У фільтрі — кілька регіонів (region.uuid in […]) */
            isFilterMultiple: true,
            /** Опції фільтра: і неактивні — задачі лишаються за ними */
            filterOptionsFilter: { isRemoved: false },
        },
        driver: {
            label: 'Водій',
            type: 'select',
            resource: 'Setting.User',
            isRequired: true,
            // Base driver filter. AssignmentCreateTemplate overrides this reactively
            // (adding carrier.code) via mergeInputProps when the task is hired.
            inputProps: {
                filter: { filterRoles: { op: 'like', value: 'ROLE_DRIVER' }, status: 'active' },
            },
            /** У фільтрі — кілька водіїв (driver.uuid in […]) */
            isFilterMultiple: true,
            /** Опції фільтра: водії будь-якого статусу, крім видаленого — задачі лишаються й за деактивованими */
            filterOptionsFilter: {
                filterRoles: { op: 'like', value: 'ROLE_DRIVER' },
                status: ['invited', 'active', 'inactive'],
            },
        },
        // Зміна водія задачі (необов'язкова, в ERP не передається). Фільтр опцій за водієм
        // і блокування без водія ставить AssignmentCreateTemplate. У фільтр журналу не входить.
        shift: {
            label: 'Зміна',
            type: 'select',
            resource: 'Shift.Shift',
            inputProps: {
                responseLabel: (item: any) => shiftLabel(item) || '—',
            },
        },
        loadingPoint: {
            label: 'Пункт навантаження',
            type: 'select',
            resource: 'Setting.ListLoadingPoint',
        },
        unloadingPoint: {
            label: 'Пункт розвантаження',
            type: 'select',
            resource: 'Setting.ListLoadingPoint',
        },
        // Типовий маршрут за поточною парою пунктів. За замовчуванням заповнюється
        // значенням, підібраним бекендом (typicalRoute у відповіді). Список
        // реактивно фільтрується у AssignmentCreateTemplate за loadingPoint+unloadingPoint.
        typicalRoute: {
            label: 'Типовий маршрут',
            type: 'select',
            resource: 'Transport.TypicalRoute',
            inputProps: {
                responseLabel: (item: any) => item?.title || '—',
            },
        },
        plannedLoadingDate: {
            label: 'Планова дата завантаження',
            type: 'datetime',
        },
        // Межі мають збігатися з колонками БД, інакше INSERT падає 500-кою
        // (SQLSTATE 22003) замість зрозумілого повідомлення:
        // total_weight_tonnes — DECIMAL(10,3), trips_count — INTEGER.
        // Ті самі межі продубльовані як Assert\Range на ExecutorAssignmentEntity.
        // Межі задаємо через inputProps (вони йдуть прямо в antd InputNumber);
        // top-level min/max ядро не прокидає — transformInputProps не викликається.
        totalWeightTonnes: {
            label: 'Загальна маса, т',
            type: 'number',
            inputProps: { min: 0, max: 9999999.999 },
        },
        tripsCount: {
            label: 'Кількість ходок',
            type: 'number',
            inputProps: { min: 1, max: 2147483647 },
        },
        dispatcherComment: {
            label: 'Коментар диспетчера',
            type: 'textarea',
        },
        // Контекст замовлення для standalone-задач (без заявки). У ERP-задач ці
        // дані живуть на заявці; standalone несе їх прямо на завданні.
        cargo: {
            label: 'Вантаж',
            type: 'select',
            resource: 'Setting.ListCargo',
            inputProps: {
                filter: { isActive: true },
            },
        },
        constructionObject: {
            label: "Об'єкт будівництва",
            type: 'select',
            resource: 'Setting.ListConstructionObject',
            inputProps: {
                filter: { isActive: true },
            },
        },
        productionObject: {
            label: "Об'єкт виробництва",
            type: 'select',
            resource: 'Setting.ListProductionObject',
            inputProps: {
                filter: { isActive: true },
            },
        },
        responsibleCustomer: {
            label: 'Відповідальний за замовлення',
            type: 'select',
            resource: 'Setting.User',
            inputProps: {
                filter: { status: 'active' },
                responseLabel: (item: any) =>
                    item?.fullName || `${item?.lastName || ''} ${item?.firstName || ''}`.trim() || '—',
            },
        },
        eWaybillNumber: {
            label: 'е-ТТН',
            type: 'string',
            isEditable: false,
        },
        startDateRange: {
            label: 'Дата початку виконання',
            type: 'daterange',
            filterName: 'executor.vehicleNeed.startDate',
            inputProps: {
                placeholder: ['Початок від', 'Початок до'],
            },
        },
        endDateRange: {
            label: 'Дата завершення виконання',
            type: 'daterange',
            filterName: 'executor.vehicleNeed.endDate',
            inputProps: {
                placeholder: ['Завершення від', 'Завершення до'],
            },
        },
        delayStatus: {
            label: 'За затримками',
            type: 'select',
            filterName: 'delays.status',
            /** У фільтрі — кілька статусів затримки (delays.status in […]) */
            isFilterMultiple: true,
            options: [
                { value: 'received', label: 'Є затримка' },
                { value: 'escalated', label: 'Ескальовано' },
                { value: 'resolved', label: 'Вирішено' },
            ],
            inputProps: {
                allowClear: true,
                placeholder: 'За затримками',
            },
        },
        filterDispatcher: {
            label: 'Відповідальний диспетчер',
            type: 'select',
            resource: 'Setting.User',
            filterName: 'executor.dispatcher',
            /** У фільтрі — кілька значень (executor.dispatcher.uuid in […]) */
            isFilterMultiple: true,
            inputProps: {
                allowClear: true,
                placeholder: 'Відповідальний диспетчер',
            },
        },
        filterLoadingPoint: {
            label: "Об'єкт навантаження",
            type: 'select',
            resource: 'Setting.ListLoadingPoint',
            filterName: 'executor.vehicleNeed.loadingPoint',
            inputProps: {
                allowClear: true,
                placeholder: "Об'єкт навантаження",
            },
        },
        filterUnloadingPoint: {
            label: "Об'єкт розвантаження",
            type: 'select',
            resource: 'Setting.ListLoadingPoint',
            filterName: 'executor.vehicleNeed.unloadingPoint',
            inputProps: {
                allowClear: true,
                placeholder: "Об'єкт розвантаження",
            },
        },
        filterCargo: {
            label: 'Тип вантажу',
            type: 'select',
            resource: 'Setting.ListCargo',
            filterName: 'executor.vehicleNeed.cargo',
            /** У фільтрі — кілька значень (executor.vehicleNeed.cargo.uuid in […]) */
            isFilterMultiple: true,
            inputProps: {
                allowClear: true,
                placeholder: 'Тип вантажу',
            },
        },
        // Те саме, але по власному полю завдання — для сторінки «Задачі без
        // заявок», де vehicleNeed завжди null і filterCargo нічого б не знайшов.
        // Підміняється замість filterCargo у AssignmentsWithoutRequestsPage.
        filterCargoOwn: {
            label: 'Тип вантажу',
            type: 'select',
            resource: 'Setting.ListCargo',
            filterName: 'cargo',
            /** У фільтрі — кілька значень (cargo.uuid in […]) */
            isFilterMultiple: true,
            inputProps: {
                allowClear: true,
                placeholder: 'Тип вантажу',
            },
        },
        // Маркер-поля для prefilter сторінок (не показуються в UI-фільтрі).
        // ListRequestTransformerTask читає filterName/filterOperator і будує
        // filters={"and":[{field:"executor.vehicleNeed", op:"is_not_null"|"is_null"}]}.
        //
        // Ознака standalone-задачі — executor.vehicleNeed IS NULL (а не executor IS
        // NULL): кожна standalone-задача тепер має власний executor-блок без заявки,
        // що несе відповідального диспетчера. leftJoin покриває і нові standalone
        // (executor є, vehicleNeed null), і старі (executor null → vehicleNeed null).
        hasRequest: {
            type: 'string',
            filterName: 'executor.vehicleNeed',
            filterOperator: 'is_not_null',
        },
        noRequest: {
            type: 'string',
            filterName: 'executor.vehicleNeed',
            filterOperator: 'is_null',
        },
        // Маркери переходів з головної («Потребує уваги»): поля у формі фільтра немає,
        // але значення приходить з URL (filterFromQuery) і показується чіпом, який можна зняти.
        // Мають збігатися з лічильниками DashboardSummaryAction.
        noDriver: {
            label: 'Водій',
            type: 'string',
            filterName: 'driver',
            filterOperator: 'is_null',
            options: [{ value: true, label: 'Не призначено' }],
        },
        noVehicle: {
            label: 'Транспортний засіб',
            type: 'string',
            filterName: 'vehicle',
            filterOperator: 'is_null',
            options: [{ value: true, label: 'Не призначено' }],
        },
        activeStatus: {
            label: 'Етап',
            type: 'string',
            filterName: 'status',
            filterOperator: '=',
            options: [{ value: 'active', label: 'Незавершені' }],
            /** Токен → статуси (ListRequestTransformerTask): задачі, які водій ще не завершив */
            filterValues: { active: ACTIVE_TASK_STATUSES },
        },
    },
    filter: {
        includeFields: [
            'id',
            'status',
            'isHired',
            'cluster',
            'region',
            'driver',
            'carrier',
            'vehicle',
            'startDateRange',
            'endDateRange',
            'delayStatus',
            'filterDispatcher',
            'filterLoadingPoint',
            'filterUnloadingPoint',
            'filterCargo',
        ],
    },
    table: {
        dataSourceNoTransform: true,
        showColumns: [
            'status',
            'cluster',
            'region',
            'driver',
            'vehicle',
            'shift',
            'carrier',
            'totalWeightTonnes',
            'loadingPoint',
            'unloadingPoint',
            'plannedLoadingDate',
        ],
        columns: [
            {
                title: 'ID завдання',
                dataIndex: 'id',
                key: 'id',
                width: 90,
                render: (v: any) => v ?? '—',
            },
            {
                title: 'Дата та час створення',
                key: 'createdAt',
                width: 160,
                // У колекції бекенд віддає createdDate (окремий collection-геттер),
                // у деталі — createdAt. Підхоплюємо обидва.
                render: (_: any, row: any) => {
                    const d = row.createdDate || row.createdAt
                    return d
                        ? new Date(d).toLocaleString('uk-UA', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                          })
                        : '—'
                },
            },
            {
                title: 'ID заявки',
                key: 'requestId',
                width: 90,
                render: (_: any, row: any) => row.executor?.vehicleNeed?.transportRequest?.id ?? '—',
            },
            {
                title: 'Номер заявки в ERP',
                key: 'requestErp',
                width: 150,
                render: (_: any, row: any) => row.executor?.vehicleNeed?.transportRequest?.externalCode ?? '—',
            },
            {
                title: 'Відповідальний диспетчер',
                key: 'dispatcher',
                width: 160,
                render: (_: any, row: any) =>
                    row.executor?.dispatcher?.shortName || row.executor?.dispatcher?.fullName || '—',
            },
            {
                title: 'Статус завдання',
                dataIndex: 'status',
                key: 'status',
                width: 210,
                render: renderStatusTag,
            },
            {
                title: 'Кластер',
                dataIndex: 'cluster',
                key: 'cluster',
                width: 150,
                render: (v: any) => v?.title || '—',
            },
            {
                title: 'Регіон',
                dataIndex: 'region',
                key: 'region',
                width: 160,
                render: (v: any) => v?.title || '—',
            },
            {
                title: 'Водій',
                dataIndex: 'driver',
                key: 'driver',
                width: 160,
                render: (v: any) => v?.shortName || v?.fullName || '—',
            },
            {
                title: 'Держ. номер ТЗ',
                dataIndex: 'vehicle',
                key: 'vehicle',
                width: 130,
                render: (v: any) => v?.registrationNumber || '—',
            },
            {
                title: 'Зміна',
                dataIndex: 'shift',
                key: 'shift',
                width: 150,
                render: (v: any) => v?.code || '—',
            },
            {
                title: 'Перевізник',
                dataIndex: 'carrier',
                key: 'carrier',
                width: 150,
                render: (v: any) => v?.title || '—',
            },
            {
                title: 'Вантаж',
                key: 'cargo',
                width: 130,
                render: (_: any, row: any) => row.executor?.vehicleNeed?.cargo?.title ?? '—',
            },
            {
                title: 'Загальна маса перевезення, т',
                dataIndex: 'totalWeightTonnes',
                key: 'totalWeightTonnes',
                width: 110,
                render: (v: any) => v ?? '—',
            },
            {
                title: 'Пункт навантаження',
                key: 'loadingPoint',
                width: 150,
                render: (_: any, row: any) =>
                    row.loadingPoint?.title || row.executor?.vehicleNeed?.loadingPoint?.title || '—',
            },
            {
                title: 'Пункт розвантаження',
                key: 'unloadingPoint',
                width: 150,
                render: (_: any, row: any) =>
                    row.unloadingPoint?.title || row.executor?.vehicleNeed?.unloadingPoint?.title || '—',
            },
            {
                title: 'Дата початку',
                key: 'startDate',
                width: 120,
                render: (_: any, row: any) => {
                    const d = row.executor?.vehicleNeed?.startDate
                    return d ? new Date(d).toLocaleDateString('uk-UA') : '—'
                },
            },
            {
                title: 'Дата завершення',
                key: 'endDate',
                width: 130,
                render: (_: any, row: any) => {
                    const d = row.executor?.vehicleNeed?.endDate
                    return d ? new Date(d).toLocaleDateString('uk-UA') : '—'
                },
            },
            {
                title: 'Затримка виконання',
                key: 'delayStatus',
                width: 150,
                render: renderDelayStatus,
            },
            {
                title: 'Оцінки водія',
                key: 'driverRatings',
                width: 110,
                render: (_: any, row: any) => {
                    const ratings: any[] = row.driverRatings || []
                    if (!ratings.length) return '—'
                    const avg = ratings.reduce((s: number, r: any) => s + (r.score ?? 0), 0) / ratings.length
                    return avg.toFixed(1)
                },
            },
        ],
        scroll: { x: 'max-content' },
    },
    /** Перегляд картки без «Службової інформації»: ID і дата створення — у зведенні (DetailShell) */
    hideServiceInfoInView: true,
    detailConfig: {
        modeConfig: {
            view: {
                template: AssignmentViewTemplate,
                /** Рядки перегляду у дві колонки; ширше лише розтягує пари «підпис — значення» */
                width: 960,
                onButtons: buildAssignmentViewButtons,
            },
            // Форма в одну колонку: стандартна ширина картки (ширше лише розтягує інпути).
            // Явно, бо без неї ядро взяло б detailConfig.width (960, для перегляду)
            create: {
                template: AssignmentCreateTemplate,
                width: DEFAULT_DETAIL_WIDTH,
            },
            // Edit reuses the create template (same fields + same carrier→driver/vehicle
            // cascade + hidden dispatcherComment). The shared template branches on `mode`.
            edit: {
                template: AssignmentCreateTemplate,
                width: DEFAULT_DETAIL_WIDTH,
            },
        },
        mode: 'drawer',
        width: 960,
    },
    form: {
        template: AssignmentCreateTemplate,
    },
    settings: {
        showPaginationTotal: true,
    },
    // pageUrl навмисно відсутній: контейнер показується двома сторінками
    // (Pages/AssignmentsWithRequestsPage, Pages/AssignmentsWithoutRequestsPage),
    // які самі рендерять ResourceList з prefilter. Меню веде на них.
    headerPage: {
        eventHandlers: {
            onButtons: {
                // "Додати кілька" — тільки на сторінці "Задачі без заявок"
                // (visible-only через depFn), відкриває MultiAssignmentDrawer.
                // Сторінка кладе свій MultiAssignmentDrawerStore у
                // crudContext.storages через setStorage('multiAssignmentStore', ...).
                AddMultipleButton: ({ buttons, crudContext }: any) => {
                    buttons['AddMultipleButton'] = {
                        label: 'Додати кілька',
                        icon: React.createElement(TbCopyPlus, { size: 22 }),
                        sort: 90,
                        priority: 'button',
                        size: 'middle',
                        depFn: ({ crudContext }: any) =>
                            crudContext?.resource?.key === WITHOUT_REQUESTS_RESOURCE_KEY &&
                            !!crudContext?.getStorage?.('multiAssignmentStore'),
                        onClick: () => {
                            crudContext?.getStorage?.('multiAssignmentStore')?.openDrawer()
                        },
                    }
                },
            },
        },
    },
}
